import React, { useCallback } from 'react'
import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Text,
  Flex,
  Box,
  Image,
  useColorModeValue,
  IconButton,
  useToast,
} from '@chakra-ui/react'
import { useLanguage } from '../../../hooks/utility/useLanguage'
import { LanguageCode } from '../../../core/i18n/types'

interface LanguageSelectorProps {
  buttonSize?: 'xs' | 'sm' | 'md' | 'lg'
  showText?: boolean
  variant?: 'ghost' | 'outline' | 'solid'
  textColor?: string
  circleFlags?: boolean
  fullWidth?: boolean
}

const LanguageSelectorComponent: React.FC<LanguageSelectorProps> = ({
  buttonSize = 'md',
  showText = true,
  variant = 'ghost',
  textColor,
  circleFlags = false,
  fullWidth = true,
}) => {
  const { availableLanguages, currentLanguage, changeLanguage, t } = useLanguage()
  const toast = useToast()

  // Use color mode values for proper contrast in all themes
  const defaultTextColor = useColorModeValue('gray.700', 'white')
  const menuBg = useColorModeValue('white', 'gray.800')
  const menuItemHoverBg = useColorModeValue('gray.50', 'gray.700')

  // Use provided textColor or default based on color mode
  const finalTextColor = textColor || defaultTextColor

  const handleLanguageChange = useCallback(
    (language: LanguageCode) => {
      changeLanguage(language)
      toast({
        title: t('profile.languageChanged') || 'Language changed',
        status: 'success',
        duration: 2000,
      });
    },
    [changeLanguage, toast, t],
  )

  // Circle flag style selector
  if (circleFlags) {
    return (
      <Menu placement='bottom'>
        <MenuButton
          as={IconButton}
          size={buttonSize}
          variant={variant}
          aria-label={t('common.language')}
          borderRadius='full'
          p={0}
          minWidth='auto'
          overflow='hidden'
          bg='transparent'
          _hover={{ bg: 'transparent' }}
          _active={{ bg: 'transparent' }}
        >
          <Image
            src={availableLanguages[currentLanguage]?.flag}
            alt={`${availableLanguages[currentLanguage]?.nameKey} flag`}
            boxSize={buttonSize === 'xs' ? '20px' : buttonSize === 'sm' ? '24px' : '32px'}
            objectFit='cover'
            borderRadius='full'
          />
        </MenuButton>
        <MenuList bg={menuBg} boxShadow='md' borderRadius='md' border='none' p={2}>
          <Flex gap={2} justify='center' wrap='wrap' p={1}>
            {Object.entries(availableLanguages).map(([code, { nameKey, flag }]) => (
              <IconButton
                key={code}
                aria-label={t(nameKey)}
                icon={<Image src={flag} alt={t(nameKey)} boxSize='28px' objectFit='cover' borderRadius='full' />}
                size='sm'
                borderRadius='full'
                variant='ghost'
                onClick={() => handleLanguageChange(code as LanguageCode)}
                bg={code === currentLanguage ? 'transparent' : 'transparent'}
                _hover={{ bg: 'transparent' }}
                _active={{ bg: 'transparent' }}
                p={1}
              />
            ))}
          </Flex>
        </MenuList>
      </Menu>
    )
  }

  // Standard selector with text
  return (
    <Box width={fullWidth ? '100%' : 'auto'}>
      <Menu placement='bottom' matchWidth={true}>
        <MenuButton
          as={Button}
          size={buttonSize}
          variant={variant}
          px={3}
          color={finalTextColor}
          width='100%'
          justifyContent='flex-start'
        >
          <Flex alignItems='center' width='100%'>
            <Box mr={showText ? 2 : 0}>
              <Image
                src={availableLanguages[currentLanguage]?.flag}
                alt={`${availableLanguages[currentLanguage]?.nameKey} flag`}
                boxSize='24px'
                objectFit='cover'
                borderRadius='sm'
              />
            </Box>
            {showText && (
              <Text color={finalTextColor} noOfLines={1}>
                {t(availableLanguages[currentLanguage]?.nameKey || 'common.language')}
              </Text>
            )}
          </Flex>
        </MenuButton>
        <MenuList bg={menuBg} boxShadow='md' borderRadius='md' border='none' w='100%'>
          {Object.entries(availableLanguages).map(([code, { nameKey, flag }]) => (
            <MenuItem
              key={code}
              onClick={() => handleLanguageChange(code as LanguageCode)}
              fontWeight={code === currentLanguage ? 'bold' : 'normal'}
              _hover={{ bg: menuItemHoverBg }}
              color={finalTextColor}
              width='100%'
              px={3}
              py={0}
              minH='44px'
              display='flex'
              alignItems='center'
            >
              <Flex alignItems='center' width='100%'>
                <Box mr={2} width='24px' height='24px' flexShrink={0}>
                  <Image src={flag} alt={`${t(nameKey)} flag`} boxSize='24px' objectFit='cover' borderRadius='sm' />
                </Box>
                <Text noOfLines={1}>{t(nameKey)}</Text>
              </Flex>
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    </Box>
  )
}

export const LanguageSelector = React.memo(LanguageSelectorComponent)
